import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { User, Sparkles, ArrowRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { TONES, ATTRIBUTIONS } from '../../data/constants';

const PLAN_INFO = {
    'Wanderer': { gens: 3, rewrites: 0 },
    'Creator': { gens: 3, rewrites: 10 },
    'Discoverer': { gens: 8, rewrites: 30 },
    'Architect': { gens: 20, rewrites: 60 },
    'Endovelicus': { gens: Infinity, rewrites: Infinity }
};

// Profile Dialog Component
const ProfileDialog = ({ open, onOpenChange, variant }) => {
    const isHub = variant === 'hub';
    const { user, updateProfile } = useAuth();
    const [name, setName] = useState('');
    const [channelName, setChannelName] = useState('');
    const [planTier, setPlanTier] = useState('Wanderer');
    const [attributionPreference, setAttributionPreference] = useState('none');
    const [preferredTone, setPreferredTone] = useState('Neutral / Authentic');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (user && open) {
            setName(user.name || '');
            setChannelName(user.channel_name || '');
            setPlanTier(user.plan_tier || 'Wanderer');
            setAttributionPreference(user.attribution_preference || 'none');
            setPreferredTone(user.preferred_tone || 'Neutral / Authentic');
        }
    }, [user, open]);

    const handleSave = async () => {
        setLoading(true);
        const success = await updateProfile(isHub ? {
            name: name,
            display_name: name // Ensure both are in sync
        } : {
            name: name,
            display_name: name, // Ensure both are in sync
            channel_name: channelName,
            plan_tier: planTier,
            attribution_preference: attributionPreference,
            preferred_tone: preferredTone
        });
        if (success) {
            onOpenChange(false);
        }
        setLoading(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto w-[95vw] sm:w-full rounded-2xl p-4 sm:p-6">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Edit Profile
                    </DialogTitle>
                    <DialogDescription>
                        {isHub ? 'Update your display name.' : 'Update your channel identity and subscription plan.'}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label htmlFor="display-name" className="text-sm font-medium">
                            Display Name
                        </Label>
                        <Input
                            id="display-name"
                            placeholder="Your full name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="mt-1 text-base sm:text-sm"
                        />
                    </div>

                    <div>
                        <Label htmlFor="public-id" className="text-sm font-medium text-muted-foreground">
                            Account Reference (User ID)
                        </Label>
                        <Input
                            id="public-id"
                            value={user?.public_id || 'Loading...'}
                            readOnly
                            disabled
                            className="mt-1 bg-muted font-mono text-xs cursor-not-allowed text-foreground/70 border-border/50"
                        />
                        <p className="text-[10px] text-muted-foreground mt-1 italic">
                            Immutable identity fingerprint.
                        </p>
                    </div>

                    <div>
                        <Label htmlFor="email" className="text-sm font-medium text-muted-foreground">
                            Email
                        </Label>
                        <Input
                            id="email"
                            value={user?.email || (user?.username?.includes('@') ? user.username : '') || ''}
                            readOnly
                            disabled
                            className="mt-1 bg-muted font-mono text-xs cursor-not-allowed text-foreground/70 border-border/50"
                        />
                        <p className="text-[10px] text-muted-foreground mt-1 italic">
                            Email cannot be changed at this time.
                        </p>
                    </div>

                    {!isHub && (
                    <div>
                        <Label htmlFor="channel-name" className="text-sm font-medium">
                            Channel Name
                        </Label>
                        <Input
                            id="channel-name"
                            placeholder="e.g. History Hub"
                            value={channelName}
                            onChange={(e) => setChannelName(e.target.value)}
                            className="mt-1 text-base sm:text-sm"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                            Displayed in the application header.
                        </p>
                    </div>
                    )}

                    {!isHub && (
                    <div>
                        <Label className="text-sm font-medium">
                            Subscription Plan
                        </Label>
                        <Card
                            className="mt-2 border-dashed cursor-pointer hover:border-indigo-500 hover:bg-indigo-50/50 transition-all group"
                            onClick={() => {
                                onOpenChange(false);
                                navigate('/subscription');
                            }}
                        >
                            <CardContent className="p-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                                        <Sparkles className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="font-bold text-slate-900">{planTier}</p>
                                        <div className="flex flex-col gap-0.5 mt-1">
                                            <p className="text-[10px] sm:text-xs text-muted-foreground flex items-center gap-1">
                                                <span>Scripts:</span>
                                                <span className="font-semibold text-slate-700">
                                                    {planTier === 'Endovelicus' || user?.entitlements?.plan_name === 'Endovelicus'
                                                        ? '∞ / ∞'
                                                        : (
                                                            <span title={`Base: ${user?.entitlements?.base_scripts || 0}${user?.entitlements?.rollover_scripts ? ` + Rollover: ${user.entitlements.rollover_scripts}` : ''}${user?.entitlements?.extra_scripts ? ` + Extra: ${user.entitlements.extra_scripts}` : ''}`}>
                                                                {user?.entitlements?.scripts_used || 0} / {(user?.entitlements?.base_scripts || 0) + (user?.entitlements?.rollover_scripts || 0) + (user?.entitlements?.extra_scripts || 0)}
                                                                {(user?.entitlements?.rollover_scripts > 0 || user?.entitlements?.extra_scripts > 0) && <span className="ml-1 text-[8px] text-indigo-400 font-black">+</span>}
                                                            </span>
                                                        )}
                                                </span>
                                            </p>
                                            <p className="text-[10px] sm:text-xs text-muted-foreground flex items-center gap-1">
                                                <span>Rewrites:</span>
                                                <span className="font-semibold text-slate-700">
                                                    {planTier === 'Endovelicus' || user?.entitlements?.plan_name === 'Endovelicus'
                                                        ? '∞ / ∞'
                                                        : (
                                                            <span title={`Base: ${user?.entitlements?.base_rewrites || 0}${user?.entitlements?.rollover_rewrites ? ` + Rollover: ${user.entitlements.rollover_rewrites}` : ''}${user?.entitlements?.extra_rewrites ? ` + Extra: ${user.entitlements.extra_rewrites}` : ''}`}>
                                                                {user?.entitlements?.rewrites_used || 0} / {(user?.entitlements?.base_rewrites || 0) + (user?.entitlements?.rollover_rewrites || 0) + (user?.entitlements?.extra_rewrites || 0)}
                                                                {(user?.entitlements?.rollover_rewrites > 0 || user?.entitlements?.extra_rewrites > 0) && <span className="ml-1 text-[8px] text-indigo-400 font-black">+</span>}
                                                            </span>
                                                        )}
                                                </span>
                                            </p>
                                        </div>
                                        <p className="text-[10px] text-indigo-500 font-medium mt-1">Click to manage subscription</p>
                                    </div>
                                </div>
                                <div className="text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <ArrowRight className="w-5 h-5" />
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                    )}

                    {!isHub && (
                    <div>
                        <Label className="text-sm font-medium">
                            Preferred Tone & Style
                        </Label>
                        <Select
                            value={preferredTone}
                            onValueChange={(val) => setPreferredTone(val)}
                        >
                            <SelectTrigger className="mt-1 h-auto min-h-[2.5rem] text-[13px] leading-snug py-1.5">
                                <SelectValue placeholder="Select tone" />
                            </SelectTrigger>
                            <SelectContent className="max-w-[var(--radix-select-trigger-width)] w-full">
                                {TONES.map(t => (
                                    <SelectItem key={t.value} value={t.value} className="text-[13px] leading-snug py-2.5 whitespace-normal">
                                        {t.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                            Your default style for new scripts.
                        </p>
                    </div>
                    )}

                    {!isHub && (
                    <div>
                        <Label className="text-sm font-medium">
                            Mention in Script
                        </Label>
                        <p className="text-xs text-muted-foreground mb-2">
                            How to mention you in generated scripts by default
                        </p>
                        <Select value={attributionPreference} onValueChange={setAttributionPreference}>
                            <SelectTrigger className="w-full">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {ATTRIBUTIONS.map(attr => (
                                    <SelectItem key={attr.value} value={attr.value}>
                                        {attr.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    )}

                    <div className="flex justify-end space-x-2 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={loading}
                        >
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Save Profile
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ProfileDialog;
